import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Club, Organization } from '@court-sync/shared';
import { getStatesForCountry } from '@court-sync/shared';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  CountrySelectField,
  isCountrySelectionBlocked,
} from '@/components/forms/CountrySelectField';
import { TimezoneSelectField } from '@/components/forms/TimezoneSelectField';
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  PhoneField,
  Textarea,
} from '@/components/ui';
import { organizationService } from '@/services/organization';
import { clubService } from '@/services/club';
interface OrgBasicFormValues {
  description: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  country: string;
  stateOrProvince: string;
  clubName: string;
  clubTimezone: string;
}

interface OrgBasicSettingsTabProps {
  organization: Organization;
  club: Club | null;
  onOrganizationSaved: (organization: Organization) => void;
  onClubSaved?: (club: Club) => void;
}

export function OrgBasicSettingsTab({
  organization,
  club,
  onOrganizationSaved,
  onClubSaved,
}: OrgBasicSettingsTabProps) {
  const showClubFields = Boolean(club && !club.isInternalShell);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OrgBasicFormValues>({
    defaultValues: {
      description: organization.description ?? '',
      address: organization.address ?? '',
      phone: organization.phone ?? '',
      email: organization.email ?? '',
      website: organization.website ?? '',
      country: organization.country ?? organization.region ?? '',
      stateOrProvince: organization.stateOrProvince ?? '',
      clubName: club?.name ?? '',
      clubTimezone: club?.timezone ?? '',
    },
  });

  useEffect(() => {
    reset({
      description: organization.description ?? '',
      address: organization.address ?? '',
      phone: organization.phone ?? '',
      email: organization.email ?? '',
      website: organization.website ?? '',
      country: organization.country ?? organization.region ?? '',
      stateOrProvince: organization.stateOrProvince ?? '',
      clubName: club?.name ?? '',
      clubTimezone: club?.timezone ?? '',
    });
  }, [organization, club, reset]);

  const country = watch('country', '');
  const stateOptions = getStatesForCountry(country);
  const countryBlocked = isCountrySelectionBlocked(country);

  const onSubmit = async (values: OrgBasicFormValues) => {
    const trimmedCountry = values.country?.trim().toUpperCase() || undefined;
    const trimmedState = values.stateOrProvince?.trim() || undefined;
    const regionFromCountry =
      trimmedCountry === 'US' || trimmedCountry === 'AU' ? trimmedCountry : undefined;

    const orgRes = await organizationService.updateOrganization(organization.id, {
      description: values.description.trim() || null,
      address: values.address.trim() || null,
      phone: values.phone.trim() || null,
      email: values.email.trim() || null,
      website: values.website.trim() || null,
      country: trimmedCountry,
      region: regionFromCountry,
      stateOrProvince: trimmedState,
    });

    if (!orgRes.success || !orgRes.data) {
      toast.error(orgRes.error || 'Failed to update organization contact details');
      return;
    }

    onOrganizationSaved(orgRes.data);

    if (showClubFields && club) {
      const clubPatch: { name?: string; timezone?: string } = {};
      if (values.clubName.trim() && values.clubName.trim() !== club.name) {
        clubPatch.name = values.clubName.trim();
      }
      if (values.clubTimezone.trim() && values.clubTimezone.trim() !== club.timezone) {
        clubPatch.timezone = values.clubTimezone.trim();
      }
      if (Object.keys(clubPatch).length > 0) {
        const clubRes = await clubService.updateClub(club.id, clubPatch);
        if (clubRes.success && clubRes.data) {
          onClubSaved?.(clubRes.data);
        } else {
          toast.error(clubRes.error || 'Failed to update club details');
          return;
        }
      }
    }

    toast.success('Settings updated');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl space-y-8">
      <section aria-labelledby="org-contact-heading" className="space-y-6">
        <div>
          <h2 id="org-contact-heading" className="text-lg font-semibold text-gray-900 dark:text-white">
            Organization contact
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Shared contact details shown on your public site footer and directory listing.
          </p>
        </div>

        <Field className="md:col-span-2">
          <FieldLabel htmlFor="org-description">Description</FieldLabel>
          <Textarea
            id="org-description"
            rows={3}
            placeholder="Brief description of your organization…"
            {...register('description')}
          />
        </Field>

        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <AddressAutocomplete
              label="Address"
              value={field.value}
              onChange={field.onChange}
              error={errors.address?.message}
              placeholder="123 Court St, City, State 12345"
            />
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <PhoneField
            control={control}
            name="phone"
            label="Phone"
            id="org-phone"
            purpose="academy_public"
            defaultCountrySource={{
              country: organization.country,
              region: organization.region,
            }}
            placeholder="Phone number"
          />

          <Field>
            <FieldLabel htmlFor="org-email">Email</FieldLabel>
            <Input id="org-email" type="email" {...register('email')} />
            {errors.email ? <FieldError>{errors.email.message}</FieldError> : null}
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <CountrySelectField
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.country?.message}
              />
            )}
          />

          {stateOptions.length > 0 ? (
            <Field>
              <FieldLabel htmlFor="org-state">State / Province</FieldLabel>
              <select id="org-state" {...register('stateOrProvince')} className="input w-full">
                <option value="">Select state / province</option>
                {stateOptions.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field>
              <FieldLabel htmlFor="org-state">State / Province (optional)</FieldLabel>
              <Input id="org-state" {...register('stateOrProvince')} className="uppercase" />
            </Field>
          )}
        </div>

        <Field>
          <FieldLabel htmlFor="org-website">Website</FieldLabel>
          <Input id="org-website" type="url" placeholder="https://…" {...register('website')} />
        </Field>
      </section>

      {showClubFields && club ? (
        <section aria-labelledby="club-name-heading" className="space-y-4 border-t border-gray-200 pt-6 dark:border-gray-700">
          <div>
            <h2 id="club-name-heading" className="text-lg font-semibold text-gray-900 dark:text-white">
              Club
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              The display name and timezone for club operations.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field className="md:col-span-2">
              <FieldLabel htmlFor="club-name">Club name</FieldLabel>
              <Input
                id="club-name"
                {...register('clubName', { required: 'Club name is required' })}
              />
              {errors.clubName ? <FieldError>{errors.clubName.message}</FieldError> : null}
            </Field>

            <Field className="md:col-span-2">
              <Controller
                name="clubTimezone"
                control={control}
                rules={{ required: 'Timezone is required' }}
                render={({ field }) => (
                  <TimezoneSelectField
                    id="club-timezone"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    country={club.country ?? organization.country ?? undefined}
                    stateOrProvince={club.stateOrProvince ?? organization.stateOrProvince ?? undefined}
                    error={errors.clubTimezone?.message}
                    autoInfer={false}
                  />
                )}
              />
              <FieldDescription>Used for court hire hours, memberships, and club calendar.</FieldDescription>
            </Field>
          </div>
        </section>
      ) : null}

      <div className="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
        <Button type="submit" isLoading={isSubmitting} disabled={countryBlocked}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
